import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { listInterBusinessCharges, remindInterBusinessOwner, resolveInterBusinessCharge } from '../api/financeApi';
import useFinanceQuery from '../useFinanceQuery';
import FinanceListPage from '../components/FinanceListPage';
import DetailLinkButton from '../components/DetailLinkButton';
import MarkPaymentModal from '../components/MarkPaymentModal';
import ConfirmModal from '../components/ConfirmModal';
import MessageModal from '../components/MessageModal';
import { useFinance } from '../financeContext';
import { money } from '../format';
import { StatusBadge, OverdueBadge, remindState } from './rowBits';

const STATUS_OPTIONS = [
  { value: 'escalated', label: 'Escalated' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' }
];

export default function B2BBillingPage() {
  const { canApproveFinance, isAdmin, vertical } = useFinance();
  const notApplicable = vertical === 'real_estate';
  const q = useFinanceQuery(
    (params) => listInterBusinessCharges({ status: 'escalated', ...params }),
    { filterKeys: ['status', 'vertical', 'overdue', 'minAmount', 'maxAmount', 'dateFrom', 'dateTo', 'ownerId'] }
  );
  const [payTarget, setPayTarget] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [actionError, setActionError] = useState('');
  const [confirm, setConfirm] = useState(null);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      if (action === 'remind') await remindInterBusinessOwner(id);
      else await resolveInterBusinessCharge(id, { action });
      await q.reload();
    } catch (err) {
      setActionError(err?.response?.data?.msg || 'Unable to update that charge.');
    } finally {
      setBusyId('');
    }
  };

  const columns = [
    { key: 'ref', header: 'Order', cardHeader: true, cell: (r) => <DetailLinkButton kind="booking" entity={r} label={`#${r.bookingRef || (r.bookingId || '').slice(-6)}`} /> },
    { key: 'owes', header: 'Owes (consuming)', cell: (r) => (
      <span>{r.consumingOwnerName || '—'}<div className="small text-muted">{[r.consumingOwnerEmail, r.consumingOwnerPhone].filter(Boolean).join(' · ')}</div></span>
    ) },
    { key: 'owed', header: 'Owed (resource)', cell: (r) => (
      <span>{r.resourceOwnerName || '—'}<div className="small text-muted">{[r.resourceOwnerEmail, r.resourceOwnerPhone].filter(Boolean).join(' · ')}</div></span>
    ) },
    { key: 'items', header: 'Charges', cell: (r) => (
      <span className="small text-muted">{(r.lineItems || []).map((li, i) => <div key={i}>{(li.description || li.kind)}: {money(li.amount)}</div>)}</span>
    ) },
    { key: 'amount', header: 'Amount', cell: (r) => money(r.amount) },
    { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} extra={<OverdueBadge row={r} />} /> }
  ];

  const rowActions = !canApproveFinance ? undefined : (r) => {
    if (!['pending', 'escalated'].includes(r.status)) {
      return <span className="small text-muted">{r.reviewerNote || '—'}</span>;
    }
    const rs = remindState(r);
    return (
      <div className="d-flex flex-wrap gap-1">
        {rs.show && (
          <Button size="sm" variant="outline-secondary" disabled={busyId === r._id} onClick={() => act(r._id, 'remind')}>Remind consumer</Button>
        )}
        <Button size="sm" variant="success" onClick={() => setPayTarget({ kind: 'interBusinessCharge', id: r._id, amount: r.amount, title: `#${r.bookingRef || ''}` })}>Mark paid</Button>
        <Button size="sm" variant="outline-danger" disabled={busyId === r._id} onClick={() => setConfirm({ id: r._id, action: 'reject' })}>Reject</Button>
        {rs.hint && <div className="w-100 small text-muted">{rs.hint}</div>}
      </div>
    );
  };

  return (
    <>
      <FinanceListPage
        title="Shared-delivery billing (B2B)"
        subtitle="What one business owes another for borrowing its shared delivery person."
        q={q}
        columns={columns}
        rowActions={rowActions}
        filterFields={['status', 'vertical', 'overdue', 'amount', 'date', 'owner']}
        statusOptions={STATUS_OPTIONS}
        searchPlaceholder="Search order ref or owner"
        emptyText="Nothing here."
        notApplicable={notApplicable}
        notApplicableText="Not applicable to real estate — real estate has no shared-delivery B2B billing. Cross-vertical rows still render here in a standalone deploy."
      />
      <MarkPaymentModal
        target={payTarget}
        show={!!payTarget}
        asAdmin={isAdmin}
        onHide={() => setPayTarget(null)}
        onRecorded={() => { setPayTarget(null); q.reload(); }}
      />
      <ConfirmModal
        show={!!confirm}
        onHide={() => setConfirm(null)}
        title="Reject this B2B charge?"
        message="The consuming business will no longer owe this amount."
        confirmLabel="Reject"
        confirmVariant="danger"
        busy={!!busyId}
        onConfirm={async () => {
          if (!confirm) return;
          await act(confirm.id, confirm.action);
          setConfirm(null);
        }}
      />
      <MessageModal
        show={!!actionError}
        onHide={() => setActionError('')}
        title="Could not update charge"
        message={actionError}
      />
    </>
  );
}
